import type { ReactNode } from 'react';

export interface PrintableColumn<T> {
  key:       string;
  label:     string;
  render?:   (row: T) => string | number | ReactNode;
  width?:    string;
  align?:    'left' | 'center' | 'right';
}

interface PrintableTableProps<T> {
  columns:  PrintableColumn<T>[];
  rows:     T[];
  /** Row key extractor (default: index) */
  rowKey?:  (row: T, i: number) => string | number;
  /** Shown when rows is empty */
  emptyMsg?: string;
}

export function PrintableTable<T>({
  columns,
  rows,
  rowKey    = (_, i) => i,
  emptyMsg  = 'No records.',
}: PrintableTableProps<T>): ReactNode {
  const thStyle: React.CSSProperties = {
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#ffffff',
    backgroundColor: '#1e3a5f',
    padding: '5px 8px',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  };

  const tdStyle = (align?: string): React.CSSProperties => ({
    fontSize: '10px',
    color: '#111827',
    padding: '4px 8px',
    borderBottom: '1px solid #f3f4f6',
    textAlign: (align as React.CSSProperties['textAlign']) ?? 'left',
  });

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px' }}>
      <thead>
        <tr>
          {columns.map(col => (
            <th
              key={col.key}
              style={{ ...thStyle, width: col.width, textAlign: (col.align as React.CSSProperties['textAlign']) ?? 'left' }}
            >
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td
              colSpan={columns.length}
              style={{ ...tdStyle(), textAlign: 'center', color: '#9ca3af', padding: '12px 8px' }}
            >
              {emptyMsg}
            </td>
          </tr>
        ) : (
          rows.map((row, i) => (
            <tr
              key={rowKey(row, i)}
              style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#f9fafb' }}
            >
              {columns.map(col => (
                <td key={col.key} style={tdStyle(col.align)}>
                  {col.render ? col.render(row) : String((row as Record<string, unknown>)[col.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}
